import { DrilldownDataContext } from 'features/drilldown/data/savedExplorationState/DrilldownExplorationContext'
import { useCallback, useContext, useRef, useState } from 'react'

import {
  applyExplorationMutation,
  buildExplorationSnapshot,
  ExplorationMutationApplyResult,
  ExplorationSnapshot,
  getExplorationRevision,
} from './explorationMutation'

export const useExplorerMutation = () => {
  const {
    explorationId,
    explorationState: panels,
    setExplorationState: setPanels,
    configuration,
    setConfiguration,
  } = useContext(DrilldownDataContext)
  const currentExplorerRef = useRef({
    explorationId,
    panels,
    configuration,
    setPanels,
    setConfiguration,
  })
  const undoRef = useRef<
    | {
        explorationId: string
        panels: typeof panels
        configuration: typeof configuration
        appliedRevision: string
      }
    | undefined
  >(undefined)
  const appliedRequestIdsRef = useRef<string[]>([])
  const previousExplorationIdRef = useRef(explorationId)
  const [, setVersion] = useState(0)

  currentExplorerRef.current = {
    explorationId,
    panels,
    configuration,
    setPanels,
    setConfiguration,
  }
  if (previousExplorationIdRef.current !== explorationId) {
    undoRef.current = undefined
    previousExplorationIdRef.current = explorationId
  }

  const buildCurrentSnapshot = useCallback(():
    | ExplorationSnapshot
    | undefined => {
    const current = currentExplorerRef.current
    return current.explorationId
      ? buildExplorationSnapshot(
          current.explorationId,
          current.panels,
          current.configuration
        )
      : undefined
  }, [])

  const applyMutation = useCallback(
    (value: unknown, requestId: string): ExplorationMutationApplyResult => {
      const current = currentExplorerRef.current
      if (!current.explorationId) {
        return { ok: false, reason: 'Explorer actions are unavailable.' }
      }
      if (appliedRequestIdsRef.current.includes(requestId)) {
        return {
          ok: false,
          reason: 'This AI Explorer change was already applied.',
        }
      }
      const result = applyExplorationMutation(
        value,
        current.explorationId,
        requestId,
        current.panels,
        current.configuration
      )
      if (!result.ok) return result

      appliedRequestIdsRef.current = [
        ...appliedRequestIdsRef.current.slice(-99),
        requestId,
      ]
      undoRef.current = {
        explorationId: current.explorationId,
        panels: current.panels,
        configuration: current.configuration,
        appliedRevision: getExplorationRevision(
          result.panels,
          result.configuration
        ),
      }
      currentExplorerRef.current = {
        ...current,
        panels: result.panels,
        configuration: result.configuration,
      }
      setVersion((previous) => previous + 1)
      current.setPanels(result.panels)
      current.setConfiguration(result.configuration)
      return result
    },
    []
  )

  const current = currentExplorerRef.current
  const canUndoLastMutation =
    undoRef.current !== undefined &&
    undoRef.current.explorationId === current.explorationId &&
    undoRef.current.appliedRevision ===
      getExplorationRevision(current.panels, current.configuration)

  const undoLastMutation = useCallback(() => {
    const undo = undoRef.current
    const current = currentExplorerRef.current
    if (
      !undo ||
      undo.explorationId !== current.explorationId ||
      undo.appliedRevision !==
        getExplorationRevision(current.panels, current.configuration)
    ) {
      undoRef.current = undefined
      setVersion((previous) => previous + 1)
      return false
    }
    undoRef.current = undefined
    currentExplorerRef.current = {
      ...current,
      panels: undo.panels,
      configuration: undo.configuration,
    }
    setVersion((previous) => previous + 1)
    current.setPanels(undo.panels)
    current.setConfiguration(undo.configuration)
    return true
  }, [])

  return {
    applyMutation,
    buildCurrentSnapshot,
    undoLastMutation,
    canUndoLastMutation,
  }
}
